import React, { useEffect, useState, useMemo } from 'react';
import { Calendar, Users, Target, X, AlertCircle, Search, Filter, Trophy } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../services/api';
import { useAppContext } from '../context/AppContext';
import { Card, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import CustomLoader from './CustomLoader';
import { TeamRegistrationForm, TeamRegistrationData } from './TeamRegistrationForm';
import { format } from 'date-fns';
import DecryptedText from './DecryptedText';
import MarkdownRenderer from './MarkdownRenderer';

interface Prize {
  amount: number;
  currency: string;
}

interface Bounty {
  id: string;
  title: string;
  description: string;
  prizes: Prize[];
  status: string;
  difficulty: string;
  categories: string[];
  maxWinners: number;
  currentWinners: number;
  startDate: Date | string;
  endDate: Date | string;
  participants: number;
  isRegistered?: boolean;
  teamCount?: number;
  teams?: string[] | any[];
}

interface Event {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  startDate: Date | string;
  endDate: Date | string;
  registrationDeadline: Date | string;
  maxParticipants: number;
  currentParticipants: number;
  type: string;
  isRegistered?: boolean;
  bounties: Bounty[];
}

interface CurrentEventsBountiesData {
  events: Event[];
  totalEvents: number;
  totalBounties: number;
}
export const Events: React.FC = () => {
  const apiClient = useApiClient();
  const { userRole } = useAppContext();
  const queryClient = useQueryClient();

  // React Query for data fetching
  const { data: rawData, isLoading, error: queryError } = useQuery({
    queryKey: ['events', 'with-participation'],
    queryFn: async () => {
      // Clear custom cache before fetching to prevent 304 stale responses
      apiClient.clearCache();

      const response = await apiClient.getCurrentEventsBountiesWithParticipation();
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch current events and bounties');
      }
      return response;
    },
    select: (response) => {
      if (!response.success || !response.data) return null;
      // Map API response to our interface structure
      const mappedData: CurrentEventsBountiesData = {
        events: response.data.events.map(event => ({
          id: event.id,
          title: event.title,
          description: event.description,
          category: event.theme,
          status: event.status,
          startDate: event.startDate,
          endDate: event.endDate,
          registrationDeadline: event.registrationDeadline,
          maxParticipants: event.maxParticipants,
          currentParticipants: event.currentParticipants,
          type: event.type,
          isRegistered: event.isRegistered,
          bounties: event.bounties.map(bounty => ({
            id: bounty.id,
            title: bounty.title,
            description: bounty.description,
            prizes: bounty.value > 0 ? [{ amount: bounty.value, currency: bounty.currency }] : [],
            status: bounty.status,
            difficulty: bounty.difficulty,
            categories: bounty.tags || [],
            maxWinners: bounty.maxWinners,
            currentWinners: bounty.currentWinners,
            startDate: bounty.startDate,
            endDate: bounty.endDate,
            participants: Array.isArray(bounty.participants) ? bounty.participants.length : 0,
            teamCount: (bounty as any).teamCount ?? (((bounty as any).teams && Array.isArray((bounty as any).teams)) ? (bounty as any).teams.length : 0),
          })),
        })),
        totalEvents: response.data.totalEvents,
        totalBounties: response.data.totalBounties,
      };
      return mappedData;
    },
  });

  // Map React Query states to component states
  const data = rawData;
  const loading = isLoading;
  const [error, setError] = useState<string | null>(null);

  // Filter and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitTeamId, setSubmitTeamId] = useState<string | null>(null);
  const [submitEventId, setSubmitEventId] = useState<string | null>(null);

  // Team registration modal states
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registeringEventId, setRegisteringEventId] = useState<string | null>(null);
  const [registeringEventTitle, setRegisteringEventTitle] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTeamId, setEditTeamId] = useState<string | null>(null);

  // Expanded descriptions state
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  // Expanded bounty descriptions state
  const [expandedBountyDescriptions, setExpandedBountyDescriptions] = useState<Set<string>>(new Set());

  // Unregister/Leave Event modal states
  const [showUnregisterModal, setShowUnregisterModal] = useState(false);
  const [unregisteringEventId, setUnregisteringEventId] = useState<string | null>(null);

  // Team data for registered events (to track bounty submissions, timestamps, and team info)
  const [eventTeamData, setEventTeamData] = useState<Record<string, {
    bountyIds: string[],
    updatedAt: Date | null,
    teamName: string,
    status: string,
    teamMembers: string[],
    teamId: string
  }>>({});

  // Project form state
  const [projectTitle, setProjectTitle] = useState('');
  const [demoDescription, setDemoDescription] = useState('');
  const [githubRepoLink, setGithubRepoLink] = useState('');
  const [demoUrl, setDemoUrl] = useState('');
  const [slidesUrl, setSlidesUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [techStack, setTechStack] = useState('');
  const [selectedBountyIds, setSelectedBountyIds] = useState<string[]>([]);

  useEffect(() => {
    // Ensure modal top offset is adjusted for small screens
    const adjustModalOnResize = () => {
      const modal = document.querySelector('.submit-project-modal') as HTMLElement | null;
      if (!modal) return;
      const isMobile = window.matchMedia('(max-width: 640px)').matches;
      if (isMobile) {
        modal.style.marginTop = '24px';
        modal.style.maxHeight = 'calc(100vh - 48px)';
      } else {
        modal.style.marginTop = '120px';
        modal.style.maxHeight = 'calc(100vh - 140px)';
      }
    };

    window.addEventListener('resize', adjustModalOnResize);
    // initial adjust
    setTimeout(adjustModalOnResize, 0);
    return () => window.removeEventListener('resize', adjustModalOnResize);
  }, []);

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (showSubmitModal || showRegistrationModal || showUnregisterModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSubmitModal, showRegistrationModal, showUnregisterModal]);

  // Fetch team data for registered events to get bounty IDs and updatedAt timestamp
  useEffect(() => {
    if (!data) return;

    const fetchTeamDataForRegisteredEvents = async () => {
      const registeredEvents = data.events.filter(e => e.isRegistered);

      const teamDataPromises = registeredEvents.map(async (event) => {
        try {
          const teamCheck = await apiClient.checkUserTeamForEvent(event.id);
          if (teamCheck.success && teamCheck.data?.hasTeam && teamCheck.data?.team) {
            const team = teamCheck.data.team;
            // Extract bounty IDs from Bounties relation field
            const bountyIds = Array.isArray(team.Bounties) ? team.Bounties : [];
            const updatedAt = team.updatedAt ? new Date(team.updatedAt) : null;

            // Extract team information
            const teamName = team.teamName || team.team_name || 'Unknown Team';
            const status = team.status || team.Status || 'Solo';
            const teamId = team.id || '';

            // Extract team member count from hasTeamMembers array (array of UUIDs in Supabase)
            // Also try has_team_member_ids for snake_case naming
            const teamMembers: string[] = [];
            const hasTeamMembersArray = team.hasTeamMembers || team.has_team_member_ids || [];

            if (Array.isArray(hasTeamMembersArray)) {
              // hasTeamMembers is an array of user IDs in Supabase
              // For display, we just need the count, so create placeholder names
              teamMembers.push(...hasTeamMembersArray.map((_: any, idx: number) => `Member ${idx + 1}`));
            }

            return {
              eventId: event.id,
              data: { bountyIds, updatedAt, teamName, status, teamMembers, teamId }
            };
          }
          return null;
        } catch (err) {
          console.error(`Failed to fetch team data for event ${event.id}:`, err);
          return null;
        }
      });

      const results = await Promise.all(teamDataPromises);

      // Build new team data object
      const newTeamData: Record<string, {
        bountyIds: string[],
        updatedAt: Date | null,
        teamName: string,
        status: string,
        teamMembers: string[],
        teamId: string
      }> = {};
      results.forEach(result => {
        if (result) {
          newTeamData[result.eventId] = result.data;
        }
      });

      setEventTeamData(newTeamData);
    };

    void fetchTeamDataForRegisteredEvents();
  }, [data, apiClient]);

  const handleEventRegister = async (eventId: string, eventTitle: string) => {
    // Open team registration modal
    setRegisteringEventId(eventId);
    setRegisteringEventTitle(eventTitle);
    setShowRegistrationModal(true);
  };

  // handleBountyRegister function removed - bounty enrollment system deprecated
  // Users now select bounties during project submission only

  const handleCloseError = () => {
    setError(null);
  };

  // Team registration mutation with optimistic updates
  const registerMutation = useMutation({
    mutationFn: (registrationData: TeamRegistrationData) =>
      apiClient.createTeamRegistration(registrationData),
    onMutate: async (registrationData) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['events', 'with-participation'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['events', 'with-participation']);

      // Optimistically update to show event as registered
      queryClient.setQueryData(['events', 'with-participation'], (old: any) => {
        if (!old || !old.success || !old.data) return old;

        return {
          ...old,
          data: {
            ...old.data,
            events: old.data.events.map((event: any) =>
              event.id === registrationData.eventId
                ? { ...event, isRegistered: true }
                : event
            ),
          },
        };
      });

      // Return context with previous data for rollback
      return { previousData };
    },
    onSuccess: async () => {
      // Don't refetch - keep optimistic update until page reload
      // This prevents flickering when backend returns stale data due to Notion API propagation delay

      // Close modal
      setShowRegistrationModal(false);
      setRegisteringEventId(null);
      setRegisteringEventTitle('');
    },
    onError: (err: Error, _variables, context: any) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(['events', 'with-participation'], context.previousData);
      }

      console.error('Team registration failed:', err);
      setError(err.message || 'Registration failed');
    },
  });

  // Team update mutation
  const updateTeamMutation = useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: TeamRegistrationData }) =>
      apiClient.updateTeamRegistration(teamId, {
        teamName: data.teamName,
        lookingForSkills: data.lookingForSkills,
        teamMemberEmails: data.teamMemberEmails,
        status: data.status,
      }),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['events', 'with-participation'] });
      // Close modal
      setShowRegistrationModal(false);
      setRegisteringEventId(null);
      setRegisteringEventTitle('');
      setIsEditMode(false);
      setEditTeamId(null);
    },
    onError: (err: Error) => {
      console.error('Team update failed:', err);
      setError(err.message || 'Update failed');
    },
  });

  // Handle team registration submission
  const handleRegistrationSubmit = async (data: TeamRegistrationData) => {
    if (isEditMode && editTeamId) {
      updateTeamMutation.mutate({ teamId: editTeamId, data });
    } else {
      registerMutation.mutate(data);
    }
  };

  // Handle team registration cancellation
  const handleRegistrationCancel = () => {
    setShowRegistrationModal(false);
    setRegisteringEventId(null);
    setRegisteringEventTitle('');
    setIsEditMode(false);
    setEditTeamId(null);
  };

  // Event unregister/leave mutation with optimistic updates
  const unregisterMutation = useMutation({
    mutationFn: (eventId: string) => apiClient.resignFromEvent(eventId),
    onMutate: async (eventId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['events', 'with-participation'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['events', 'with-participation']);

      // Optimistically update to show event as unregistered
      queryClient.setQueryData(['events', 'with-participation'], (old: any) => {
        if (!old || !old.success || !old.data) return old;

        return {
          ...old,
          data: {
            ...old.data,
            events: old.data.events.map((event: any) =>
              event.id === eventId
                ? { ...event, isRegistered: false }
                : event
            ),
          },
        };
      });

      // Return context with previous data for rollback
      return { previousData };
    },
    onSuccess: async () => {
      // Don't refetch - keep optimistic update until page reload
      // This prevents flickering when backend returns stale data due to Notion API propagation delay

      // Close modal
      setShowUnregisterModal(false);
      setUnregisteringEventId(null);
    },
    onError: (err: Error, _eventId, context: any) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(['events', 'with-participation'], context.previousData);
      }

      console.error('Error leaving event:', err);
      setError(err.message || 'An error occurred while leaving the event');
    },
  });

  // Handle event unregister/leave
  const handleUnregister = async () => {
    if (!unregisteringEventId) return;
    unregisterMutation.mutate(unregisteringEventId);
  };

  // Project submission mutation with optimistic updates
  const submitProjectMutation = useMutation({
    mutationFn: ({ teamId, projectData }: { teamId: string; projectData: any }) =>
      apiClient.submitProjectForTeam(teamId, projectData),
    onMutate: async ({ projectData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['events', 'with-participation'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['events', 'with-participation']);

      // Get current local timestamp for optimistic update
      const now = new Date();

      // Optimistically update eventTeamData with new timestamp and bounties
      setEventTeamData(prev => {
        const currentEventData = prev[submitEventId || ''];
        if (!currentEventData || !submitEventId) return prev;

        return {
          ...prev,
          [submitEventId]: {
            ...currentEventData,
            bountyIds: projectData.bountyIds || currentEventData.bountyIds,
            updatedAt: now,  // Use local timestamp for instant feedback
          },
        };
      });

      // Return context with previous data for rollback
      return { previousData };
    },
    onSuccess: async () => {
      // Don't refetch - keep optimistic update until page reload
      // This prevents flickering when backend returns stale data due to Notion API propagation delay

      // Close modal and reset form
      setShowSubmitModal(false);
      setProjectTitle('');
      setDemoDescription('');
      setGithubRepoLink('');
      setDemoUrl('');
      setSlidesUrl('');
      setVideoUrl('');
      setTechStack('');
      setSelectedBountyIds([]);
    },
    onError: (err: Error, _variables, context: any) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(['events', 'with-participation'], context.previousData);
      }

      console.error('Project submission failed:', err);
      setError(err.message || 'Submission failed');
    },
  });

  // Toggle event description expansion
  const toggleDescriptionExpansion = (eventId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  // Toggle bounty description expansion
  const toggleBountyDescriptionExpansion = (bountyId: string) => {
    setExpandedBountyDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bountyId)) {
        newSet.delete(bountyId);
      } else {
        newSet.add(bountyId);
      }
      return newSet;
    });
  };

  // Filter and search logic
  const filteredEvents = useMemo(() => {
    if (!data) return [];

    return data.events.filter(event => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = event.title.toLowerCase().includes(query);
        const matchesDescription = event.description.toLowerCase().includes(query);
        if (!matchesTitle && !matchesDescription) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && event.status.toLowerCase() !== statusFilter.toLowerCase()) {
        return false;
      }

      // Date range filter
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);

      if (startDateFilter) {
        const filterStart = new Date(startDateFilter);
        if (eventStart < filterStart) return false;
      }

      if (endDateFilter) {
        const filterEnd = new Date(endDateFilter);
        if (eventEnd > filterEnd) return false;
      }

      return true;
    });
  }, [data, searchQuery, statusFilter, startDateFilter, endDateFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  // Get ALL bounties for the event being submitted
  // IMPORTANT: This must be before any early returns to maintain hook order
  // Bounty enrollment removed - users now select bounties during submission only
  const eventBounties = useMemo(() => {
    if (!submitEventId || !data) return [];
    const event = data.events.find(e => e.id === submitEventId);
    if (!event) return [];
    return event.bounties; // Return ALL bounties, not just enrolled ones
  }, [submitEventId, data]);

  if (loading) {
    return <CustomLoader />;
  }

  if (!data || data.events.length === 0) {
    return (
      <div className="space-y-3">
        <div className="text-center p-8 text-gray-600">No current events available</div>
      </div>
    );
  }

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || startDateFilter || endDateFilter;

  const formatDate = (date: Date | string) => {
    // Show month and day only (hide year)
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const isSameDay = (a: Date, b: Date) => {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };

  const isToday = (d: Date) => isSameDay(d, new Date());

  const hasTimeInfo = (raw: Date | string) => {
    if (!raw) return false;
    if (typeof raw === 'string') {
      // ISO with time contains 'T' or explicit hh:mm
      return raw.includes('T') || /\d{1,2}:\d{2}/.test(raw);
    }
    if (raw instanceof Date) {
      return !(raw.getHours() === 0 && raw.getMinutes() === 0 && raw.getSeconds() === 0);
    }
    return false;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const renderEventDate = (event: Event) => {
    const startRaw = event.startDate;
    const endRaw = event.endDate;
    const start = new Date(startRaw);
    const end = new Date(endRaw);

    const startHasTime = hasTimeInfo(startRaw);
    const endHasTime = hasTimeInfo(endRaw);

    // If start is today
    if (isToday(start)) {
      // If end is same day: show Today: start — end (times if available)
      if (isSameDay(start, end)) {
        if (startHasTime && endHasTime) {
          return `Today: ${formatTime(start)} – ${formatTime(end)}`;
        }
        // times not available: show Today and date
        return `Today: ${formatDate(start)}`;
      }

      // End is on a different day
      // Show Today: <startTime if available> — ends <EndDate> [endTime if available]
      const startPart = startHasTime ? formatTime(start) : formatDate(start);
      const endPartDate = formatDate(end);
      const endPartTime = endHasTime ? ` ${formatTime(end)}` : '';
      return `Today: ${startPart} — ends ${endPartDate}${endPartTime}`;
    }

    // Start is not today
    // If start and end are same day
    if (isSameDay(start, end)) {
      if (startHasTime && endHasTime) {
        return `${formatDate(start)}: ${formatTime(start)} – ${formatTime(end)}`;
      }
      return formatDate(start);
    }

    // Multi-day event: show startDate — endDate and times if available
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    const timeStr = (startHasTime && endHasTime) ? ` (${formatTime(start)} – ${formatTime(end)})` : '';
    return `${startStr} – ${endStr}${timeStr}`;
  };

  // Small countdown component showing time remaining until given deadline
  const SubmissionCountdown: React.FC<{ deadline: Date }> = ({ deadline }) => {
    const [remaining, setRemaining] = useState<number>(Math.max(0, deadline.getTime() - Date.now()));

    useEffect(() => {
      let mounted = true;
      if (!deadline) return;
      const tick = () => {
        if (!mounted) return;
        setRemaining(Math.max(0, deadline.getTime() - Date.now()));
      };
      const id = window.setInterval(tick, 1000);
      tick();
      return () => {
        mounted = false;
        clearInterval(id);
      };
    }, [deadline]);

    if (remaining <= 0) {
      // When countdown reaches zero, don't render a separate "Submission closed" text here.
      // The Submit Project button will be disabled instead to indicate the closed state.
      return null;
    }

    // Display rules:
    // - > 1 hour: show hours with one decimal (e.g. "2.5 hours left")
    // - <= 1 hour and > 10 minutes: show minutes only (e.g. "45 minutes left")
    // - <= 10 minutes: show minutes and seconds (e.g. "9m 45s left")

    const oneHour = 60 * 60 * 1000;
    const tenMinutes = 10 * 60 * 1000;

    let label = '';

    if (remaining > oneHour) {
      const hrs = remaining / oneHour;
      const hrsRounded = Math.round(hrs * 10) / 10; // 1 decimal
      const hrsStr = Number.isInteger(hrsRounded) ? `${hrsRounded.toFixed(0)}` : `${hrsRounded.toFixed(1)}`;
      label = `${hrsStr} ${hrsRounded === 1 ? 'hour' : 'hours'} left`;
    } else if (remaining > tenMinutes) {
      const mins = Math.ceil(remaining / 60000);
      label = `${mins} ${mins === 1 ? 'minute' : 'minutes'} left`;
    } else {
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      if (mins > 0) {
        label = `${mins}m ${secs}s left`;
      } else {
        label = `${secs}s left`;
      }
    }

    return (
      <span className="text-xs text-yellow-400 ml-2">
        Submit by {deadline.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} ({label})
      </span>
    );
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 mt-[85px] pb-12">
      <div className="max-w-4xl mx-auto">
        {/* Page Title (renamed and styled) */}
        <div className="mt-4 mb-2">
          <DecryptedText
            text="Your Events"
            animateOn="view"
            revealDirection="start"
            speed={100}
            sequential={true}
            className="text-black text-3xl md:text-3xl font-bold text-left"
          />
        </div>
        {/* Error Modal Popup with Spotlight Effect */}
        {error && (
          <>
            {/* Backdrop with cutout for highlighted button */}
            <div
              className="fixed inset-0 z-40"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(2px)'
              }}
              onClick={handleCloseError}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 pointer-events-none">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200 pointer-events-auto">
                {/* Close Button */}
                <button
                  onClick={handleCloseError}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Icon and Title */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Registration Error
                    </h3>
                    <p className="text-sm text-gray-600">
                      {error}
                    </p>
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    onClick={handleCloseError}
                    className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 hover:opacity-90 text-white"
                  >
                    Got it
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Search and Filter Bar: show only when there are more than 10 events or filters/search is active */}
        {(data.events.length > 10 || hasActiveFilters) && (
          <div className="mb-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1 w-full md:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black z-10" />
                <input
                  type="text"
                  placeholder="Search events by title or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-black placeholder:text-black/50"
                />
              </div>

              {/* Filter Toggle and Clear Button (inline) */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Filters</span>
                  <span className="sm:hidden">Filter</span>
                  {hasActiveFilters && (
                    <Badge variant="default" className="ml-1 bg-purple-500">
                      Active
                    </Badge>
                  )}
                </Button>

                {hasActiveFilters && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <Card className="p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-white placeholder:text-white/50"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  {/* Start Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date From
                    </label>
                    <input
                      type="date"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white"
                    />
                  </div>

                  {/* End Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date Until
                    </label>
                    <input
                      type="date"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white"
                    />
                  </div>
                </div>
              </Card>
            )}

            {/* Results Count */}
            {hasActiveFilters && (
              <div className="text-sm text-gray-600">
                Showing {filteredEvents.length} of {data.events.length} events
              </div>
            )}
          </div>
        )}

        <div className="space-y-2 mt-6 w-full">

          {filteredEvents.length === 0 ? (
            <div className="text-center p-8 text-gray-600">
              No events match your filters. Try adjusting your search criteria.
            </div>
          ) : (
            <>
              {/* Registered Events */}
              {filteredEvents.filter(e => e.isRegistered).map((event) => (
                <Card
                  key={event.id}
                  className="overflow-hidden bg-white group font-inter"
                >
                  <CardHeader className="p-3">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Event Info */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <CardTitle className="text-2xl truncate font-space text-left">{event.title}</CardTitle>
                          </div>

                          {/* Event Description */}
                          {event.description && (
                            <div className="mb-2">
                              <div className={`text-sm text-muted-foreground ${expandedDescriptions.has(event.id) ? '' : 'line-clamp-2'} text-left`}>
                                <MarkdownRenderer content={event.description} />
                              </div>
                              {event.description.length > 150 && (
                                <button
                                  onClick={() => toggleDescriptionExpansion(event.id)}
                                  className="text-blue-600 hover:text-blue-700 text-xs mt-0.5 block text-left font-medium"
                                >
                                  {expandedDescriptions.has(event.id) ? (
                                    <>Show Less ↑</>
                                  ) : (
                                    <>Read More →</>
                                  )}
                                </button>
                              )}
                            </div>
                          )}

                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {renderEventDate(event)}
                              </span>
                              {/* If the event starts today and is a single-day event with times, show a countdown to (end time - 1 hour) for submit deadline */}
                              {(() => {
                                try {
                                  const startRaw: any = (event as any).startDate;
                                  const endRaw: any = (event as any).endDate;
                                  const startISO: string | null = (event as any).startDateISO ?? null;
                                  const endISO: string | null = (event as any).endDateISO ?? null;
                                  const startHasTime: boolean = (event as any).startDateHasTime ?? hasTimeInfo(startRaw);
                                  const endHasTime: boolean = (event as any).endDateHasTime ?? hasTimeInfo(endRaw);

                                  const start = startISO ? new Date(startISO) : new Date(startRaw);
                                  const end = endISO ? new Date(endISO) : new Date(endRaw);

                                  if (isToday(start) && isSameDay(start, end) && endHasTime) {
                                    const deadline = new Date(end.getTime() - 60 * 60 * 1000); // end - 1 hour
                                    if (deadline.getTime() > Date.now()) {
                                      return <SubmissionCountdown deadline={deadline} />;
                                    }
                                  }
                                } catch (err) {
                                  // noop - fall through
                                }
                                return null;
                              })()}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              <span>
                                {event.currentParticipants}/{event.maxParticipants}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {event.category?.split(/[&,]+/).map((tag, i) => (
                                <span key={i} className={`px-1.5 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}>
                                  {tag.trim()}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Team Information Display - PM-9 */}
                          {eventTeamData[event.id] && (
                            <div className="pt-2">
                              {eventTeamData[event.id].status === 'Solo' ? (
                                <div className="text-xs">
                                  <div className="flex items-center gap-1 text-green-600 font-medium mb-1">
                                    <span className="text-green-600">✓</span>
                                    <span>Registered as Solo Developer</span>
                                    <button
                                      onClick={() => {
                                        setRegisteringEventId(event.id);
                                        setRegisteringEventTitle(event.title);
                                        setShowRegistrationModal(true);
                                        setIsEditMode(true);
                                        setEditTeamId(eventTeamData[event.id].teamId);
                                      }}
                                      className="ml-2 text-xs text-purple-600 hover:text-purple-800 underline flex items-center gap-1"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                      Edit
                                    </button>
                                  </div>
                                </div>
                              ) : eventTeamData[event.id].status === 'Looking for a Team' ? (
                                <div className="text-xs">
                                  <div className="flex items-center gap-1 text-blue-600 font-medium mb-1">
                                    <span className="text-blue-600">✓</span>
                                    <span>Registered - Looking for a Team</span>
                                    <button
                                      onClick={() => {
                                        setRegisteringEventId(event.id);
                                        setRegisteringEventTitle(event.title);
                                        setShowRegistrationModal(true);
                                        setIsEditMode(true);
                                        setEditTeamId(eventTeamData[event.id].teamId);
                                      }}
                                      className="ml-2 text-xs text-purple-600 hover:text-purple-800 underline flex items-center gap-1"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                      Edit
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs">
                                  <div className="flex items-center gap-1 text-green-600 font-medium mb-0.5">
                                    <span className="text-green-600">✓</span>
                                    <span>Team: "{eventTeamData[event.id].teamName}" ({eventTeamData[event.id].teamMembers.length} {eventTeamData[event.id].teamMembers.length === 1 ? 'member' : 'members'})</span>
                                    <button
                                      onClick={() => {
                                        setRegisteringEventId(event.id);
                                        setRegisteringEventTitle(event.title);
                                        setShowRegistrationModal(true);
                                        setIsEditMode(true);
                                        setEditTeamId(eventTeamData[event.id].teamId);
                                      }}
                                      className="ml-2 text-xs text-purple-600 hover:text-purple-800 underline flex items-center gap-1"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                      Edit
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: Quick Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {event.isRegistered ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  const teamCheck = await apiClient.checkUserTeamForEvent(event.id);
                                  if (!teamCheck.success || !teamCheck.data?.hasTeam || !teamCheck.data?.team) {
                                    setError('You must be part of a team to submit a project');
                                    return;
                                  }

                                  const team = teamCheck.data.team;
                                  setSubmitTeamId(team.id || event.id);
                                  setSubmitEventId(event.id);

                                  // Pre-populate form fields with existing project data
                                  setProjectTitle(team['Project Title'] || team.projectTitle || team.project_title || '');
                                  setDemoDescription(team['Demo Description'] || team.demoDescription || team.demo_description || '');
                                  setGithubRepoLink(team['Github Repo Link'] || team.githubRepoLink || team.github_repo_link || '');
                                  setDemoUrl(team['Demo URL'] || team.demoUrl || team.demo_url || '');
                                  setSlidesUrl(team.Slides || team.slidesUrl || team.slides_url || '');
                                  setVideoUrl(team['Video URL'] || team.videoUrl || team.video_url || '');
                                  setTechStack(team['Tech Stack'] || team.techStack || team.tech_stack || '');

                                  // Pre-select bounties that are already in team's Bounties relation
                                  // Handle both Notion relation (Bounties) and Supabase array (bounty_ids)
                                  const existingBountyIds = Array.isArray(team.Bounties) ? team.Bounties : (team.bounty_ids || []);
                                  setSelectedBountyIds(existingBountyIds);

                                  setShowSubmitModal(true);
                                } catch (err) {
                                  setError('Failed to verify team membership');
                                }
                              }}
                              className={`bg-[#93D6D9] hover:opacity-95 text-black border-transparent ${(submitProjectMutation.isPending || (() => {
                                try {
                                  const startRaw: any = (event as any).startDate;
                                  const endRaw: any = (event as any).endDate;
                                  const startISO: string | null = (event as any).startDateISO ?? null;
                                  const endISO: string | null = (event as any).endDateISO ?? null;
                                  const startHasTime: boolean = (event as any).startDateHasTime ?? hasTimeInfo(startRaw);
                                  const endHasTime: boolean = (event as any).endDateHasTime ?? hasTimeInfo(endRaw);

                                  const start = startISO ? new Date(startISO) : new Date(startRaw);
                                  const end = endISO ? new Date(endISO) : new Date(endRaw);
                                  const now = Date.now();

                                  // Disable if event hasn't started yet
                                  if (start.getTime() > now) {
                                    return true;
                                  }

                                  // Disable if submission deadline has passed (for same-day events)
                                  if (isToday(start) && isSameDay(start, end) && endHasTime) {
                                    const deadline = new Date(end.getTime() - 60 * 60 * 1000); // end - 1 hour
                                    return deadline.getTime() <= now;
                                  }
                                } catch (err) {
                                  // if anything goes wrong, don't disable
                                }
                                return false;
                              })()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                              disabled={submitProjectMutation.isPending || (() => {
                                try {
                                  const startRaw: any = (event as any).startDate;
                                  const endRaw: any = (event as any).endDate;
                                  const startISO: string | null = (event as any).startDateISO ?? null;
                                  const endISO: string | null = (event as any).endDateISO ?? null;
                                  const startHasTime: boolean = (event as any).startDateHasTime ?? hasTimeInfo(startRaw);
                                  const endHasTime: boolean = (event as any).endDateHasTime ?? hasTimeInfo(endRaw);

                                  const start = startISO ? new Date(startISO) : new Date(startRaw);
                                  const end = endISO ? new Date(endISO) : new Date(endRaw);
                                  const now = Date.now();

                                  // Disable if event hasn't started yet
                                  if (start.getTime() > now) {
                                    return true;
                                  }

                                  // Disable if submission deadline has passed (for same-day events)
                                  if (isToday(start) && isSameDay(start, end) && endHasTime) {
                                    const deadline = new Date(end.getTime() - 60 * 60 * 1000); // end - 1 hour
                                    return deadline.getTime() <= now;
                                  }
                                } catch (err) {
                                  // if anything goes wrong, don't disable
                                }
                                return false;
                              })()}
                            >
                              Submit Project
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setUnregisteringEventId(event.id);
                                setShowUnregisterModal(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Leave Event
                            </Button>
                          </>
                        ) : (
                          <Button
                            id={`event-register-${event.id}`}
                            size="sm"
                            className="bg-[#782c8c] hover:bg-[#5e236a] text-white"
                            onClick={() => handleEventRegister(event.id, event.title)}
                          >
                            Register
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <div className="border-t">
                    {/* Bounties Section */}
                    <div className="px-3 pb-2 bg-gray-50">
                      <h3 className="text-md font-space text-black pt-2 pb-1 text-left">Bounties</h3>
                      <div className="space-y-0">
                        {(event.bounties && event.bounties.length > 0) ? event.bounties.map((bounty, idx) => (
                          <div key={bounty.id} className="py-2 px-2 group">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2">
                                  <div className="flex-shrink-0 mt-0.5">
                                    <Trophy className="h-4 w-4 text-yellow-600 mr-2" />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="font-semibold text-sm truncate text-left">{bounty.title}</h4>
                                    <div className={`text-xs text-muted-foreground ${expandedBountyDescriptions.has(bounty.id) ? '' : 'line-clamp-2'} mt-0.5 text-left`}>
                                      <MarkdownRenderer content={bounty.description} />
                                    </div>
                                    {bounty.description.length > 80 && (
                                      <button
                                        onClick={() => toggleBountyDescriptionExpansion(bounty.id)}
                                        className="text-blue-600 hover:text-blue-700 text-xs mt-0.5 block text-left font-medium"
                                      >
                                        {expandedBountyDescriptions.has(bounty.id) ? (
                                          <>Show Less ↑</>
                                        ) : (
                                          <>Read More →</>
                                        )}
                                      </button>
                                    )}

                                    <div className="flex items-center gap-2 mt-1">
                                      {bounty.prizes.map((prize, pidx) => (
                                        <div key={pidx} className="flex items-baseline gap-1 rounded bg-transparent">
                                          <span className="text-xs font-medium text-muted-foreground">Bounty:</span>
                                          <span className="text-xs font-medium text-muted-foreground">{prize.currency}</span>
                                          <span className="text-sm font-bold">{prize.amount}</span>
                                        </div>
                                      ))}
                                      {bounty.categories.map((category, cidx) => (
                                        <Badge key={cidx} variant="outline" className="text-xs">
                                          {category}
                                        </Badge>
                                      ))}

                                      {/* Teams and Winners: always visible, order: Teams • Winners */}
                                      <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                        <span>{bounty.teamCount ?? 0} Teams</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="shrink-0">
                                {/* Show timestamp if bounty was selected during submission, otherwise show "Select when submitting" */}
                                {(() => {
                                  const teamData = eventTeamData[event.id];
                                  if (teamData && teamData.bountyIds.includes(bounty.id) && teamData.updatedAt) {
                                    const formattedDate = format(teamData.updatedAt, 'MMM d, yyyy \'at\' h:mm a');
                                    return (
                                      <div className="text-xs text-green-600 font-medium">
                                        Submitted on {formattedDate}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="text-xs text-gray-500 italic">
                                      Select when submitting
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )) : null}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {/* "Register To" Section */}
              {filteredEvents.filter(e => !e.isRegistered).length > 0 && (
                <div className="mt-4 mb-2">
                  <DecryptedText
                    text="Register To"
                    animateOn="view"
                    revealDirection="start"
                    speed={100}
                    sequential={true}
                    className="text-black text-3xl md:text-3xl font-bold text-left"
                  />
                </div>
              )}

              {/* Unregistered Events */}
              {filteredEvents.filter(e => !e.isRegistered).map((event) => (
                <Card
                  key={event.id}
                  className="overflow-hidden bg-white group font-inter"
                >
                  <CardHeader className="p-3">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Event Info */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <CardTitle className="text-2xl truncate font-space text-left">{event.title}</CardTitle>
                          </div>

                          {/* Event Description */}
                          {event.description && (
                            <div className="mb-2">
                              <div className={`text-sm text-muted-foreground ${expandedDescriptions.has(event.id) ? '' : 'line-clamp-2'} text-left`}>
                                <MarkdownRenderer content={event.description} />
                              </div>
                              {event.description.length > 150 && (
                                <button
                                  onClick={() => toggleDescriptionExpansion(event.id)}
                                  className="text-blue-600 hover:text-blue-700 text-xs mt-0.5 block text-left font-medium"
                                >
                                  {expandedDescriptions.has(event.id) ? (
                                    <>Show Less ↑</>
                                  ) : (
                                    <>Read More →</>
                                  )}
                                </button>
                              )}
                            </div>
                          )}

                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {renderEventDate(event)}
                              </span>
                              {/* If the event starts today and is a single-day event with times, show a countdown to (end time - 1 hour) for submit deadline */}
                              {(() => {
                                try {
                                  const startRaw: any = (event as any).startDate;
                                  const endRaw: any = (event as any).endDate;
                                  const startISO: string | null = (event as any).startDateISO ?? null;
                                  const endISO: string | null = (event as any).endDateISO ?? null;
                                  const startHasTime: boolean = (event as any).startDateHasTime ?? hasTimeInfo(startRaw);
                                  const endHasTime: boolean = (event as any).endDateHasTime ?? hasTimeInfo(endRaw);

                                  const start = startISO ? new Date(startISO) : new Date(startRaw);
                                  const end = endISO ? new Date(endISO) : new Date(endRaw);

                                  if (isToday(start) && isSameDay(start, end) && endHasTime) {
                                    const deadline = new Date(end.getTime() - 60 * 60 * 1000); // end - 1 hour
                                    if (deadline.getTime() > Date.now()) {
                                      return <SubmissionCountdown deadline={deadline} />;
                                    }
                                  }
                                } catch (err) {
                                  // noop - fall through
                                }
                                return null;
                              })()}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              <span>
                                {event.currentParticipants}/{event.maxParticipants}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {event.category?.split(/[&,]+/).map((tag, i) => (
                                <span key={i} className={`px-1.5 py-0.5 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}>
                                  {tag.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Quick Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {event.isRegistered ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const teamCheck = await apiClient.checkUserTeamForEvent(event.id);
                                if (!teamCheck.success || !teamCheck.data?.hasTeam || !teamCheck.data?.team) {
                                  setError('You must be part of a team to submit a project');
                                  return;
                                }

                                const team = teamCheck.data.team;
                                setSubmitTeamId(team.id || event.id);
                                setSubmitEventId(event.id);

                                // Pre-populate form fields with existing project data
                                setProjectTitle(team['Project Title'] || team.projectTitle || team.project_title || '');
                                setDemoDescription(team['Demo Description'] || team.demoDescription || team.demo_description || '');
                                setGithubRepoLink(team['Github Repo Link'] || team.githubRepoLink || team.github_repo_link || '');
                                setDemoUrl(team['Demo URL'] || team.demoUrl || team.demo_url || '');
                                setSlidesUrl(team.Slides || team.slidesUrl || team.slides_url || '');
                                setVideoUrl(team['Video URL'] || team.videoUrl || team.video_url || '');
                                setTechStack(team['Tech Stack'] || team.techStack || team.tech_stack || '');

                                // Pre-select bounties that are already in team's Bounties relation
                                const existingBountyIds = Array.isArray(team.Bounties) ? team.Bounties : (team.bounty_ids || []);
                                setSelectedBountyIds(existingBountyIds);

                                setShowSubmitModal(true);
                              } catch (err) {
                                setError('Failed to verify team membership');
                              }
                            }}
                            className={`bg-[#a3d5d9] hover:opacity-95 text-black border-transparent ${(submitProjectMutation.isPending || (() => {
                              try {
                                const startRaw: any = (event as any).startDate;
                                const endRaw: any = (event as any).endDate;
                                const startISO: string | null = (event as any).startDateISO ?? null;
                                const endISO: string | null = (event as any).endDateISO ?? null;
                                const startHasTime: boolean = (event as any).startDateHasTime ?? hasTimeInfo(startRaw);
                                const endHasTime: boolean = (event as any).endDateHasTime ?? hasTimeInfo(endRaw);

                                const start = startISO ? new Date(startISO) : new Date(startRaw);
                                const end = endISO ? new Date(endISO) : new Date(endRaw);
                                const now = Date.now();

                                // Disable if event hasn't started yet
                                if (start.getTime() > now) {
                                  return true;
                                }

                                // Disable if submission deadline has passed (for same-day events)
                                if (isToday(start) && isSameDay(start, end) && endHasTime) {
                                  const deadline = new Date(end.getTime() - 60 * 60 * 1000); // end - 1 hour
                                  return deadline.getTime() <= now;
                                }
                              } catch (err) {
                                // if anything goes wrong, don't disable
                              }
                              return false;
                            })()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={submitProjectMutation.isPending || (() => {
                              try {
                                const startRaw: any = (event as any).startDate;
                                const endRaw: any = (event as any).endDate;
                                const startISO: string | null = (event as any).startDateISO ?? null;
                                const endISO: string | null = (event as any).endDateISO ?? null;
                                const startHasTime: boolean = (event as any).startDateHasTime ?? hasTimeInfo(startRaw);
                                const endHasTime: boolean = (event as any).endDateHasTime ?? hasTimeInfo(endRaw);

                                const start = startISO ? new Date(startISO) : new Date(startRaw);
                                const end = endISO ? new Date(endISO) : new Date(endRaw);
                                const now = Date.now();

                                // Disable if event hasn't started yet
                                if (start.getTime() > now) {
                                  return true;
                                }

                                // Disable if submission deadline has passed (for same-day events)
                                if (isToday(start) && isSameDay(start, end) && endHasTime) {
                                  const deadline = new Date(end.getTime() - 60 * 60 * 1000); // end - 1 hour
                                  return deadline.getTime() <= now;
                                }
                              } catch (err) {
                                // if anything goes wrong, don't disable
                              }
                              return false;
                            })()}
                          >
                            Submit Project
                          </Button>
                        ) : (
                          <Button
                            id={`event-register-${event.id}`}
                            size="sm"
                            className="bg-[#782c8c] hover:bg-[#5e236a] text-white"
                            onClick={() => handleEventRegister(event.id, event.title)}
                          >
                            Register
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <div className="border-t">
                    {/* Bounties Section */}
                    <div className="px-3 pb-2 bg-gray-50">
                      <h3 className="text-md font-space text-black pt-2 pb-1 text-left">Bounties</h3>
                      <div className="space-y-0">
                        {(event.bounties && event.bounties.length > 0) ? event.bounties.map((bounty, idx) => (
                          <div key={bounty.id} className="py-2 px-2 group">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2">
                                  <div className="flex-shrink-0 mt-0.5">
                                    <Trophy className="h-4 w-4 text-yellow-600 mr-2" />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="font-semibold text-sm truncate text-left">{bounty.title}</h4>
                                    <div className={`text-xs text-muted-foreground ${expandedBountyDescriptions.has(bounty.id) ? '' : 'line-clamp-2'} mt-0.5 text-left`}>
                                      <MarkdownRenderer content={bounty.description} />
                                    </div>
                                    {bounty.description.length > 80 && (
                                      <button
                                        onClick={() => toggleBountyDescriptionExpansion(bounty.id)}
                                        className="text-blue-600 hover:text-blue-700 text-xs mt-0.5 block text-left font-medium"
                                      >
                                        {expandedBountyDescriptions.has(bounty.id) ? (
                                          <>Show Less ↑</>
                                        ) : (
                                          <>Read More →</>
                                        )}
                                      </button>
                                    )}

                                    <div className="flex items-center gap-2 mt-1">
                                      {bounty.prizes.map((prize, pidx) => (
                                        <div key={pidx} className="flex items-baseline gap-1 rounded bg-transparent">
                                          <span className="text-xs font-medium text-muted-foreground">Bounty:</span>
                                          <span className="text-xs font-medium text-muted-foreground">{prize.currency}</span>
                                          <span className="text-sm font-bold">{prize.amount}</span>
                                        </div>
                                      ))}
                                      {bounty.categories.map((category, cidx) => (
                                        <Badge key={cidx} variant="outline" className="text-xs">
                                          {category}
                                        </Badge>
                                      ))}

                                      {/* Teams and Winners: always visible, order: Teams • Winners */}
                                      <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                        <span>{bounty.teamCount ?? 0} Teams</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="shrink-0">
                                {/* Unregistered events: removed "Select when submitting" message */}
                              </div>
                            </div>
                          </div>
                        )) : null}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </>
          )}

          {/* Summary Section */}
          {/* <div className="flex gap-6 justify-center flex-wrap p-5 bg-white border border-gray-200 rounded-lg shadow-sm max-w-lg mx-auto" data-tutorial="summary">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Events</span>
            <span className="text-3xl font-bold text-gray-900">{data.totalEvents}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Bounties</span>
            <span className="text-3xl font-bold text-gray-900">{data.totalBounties}</span>
          </div>
        </div> */}
        </div>

        {/* Submit Project Modal */}
        {showSubmitModal && (
          <div className="fixed inset-0 z-80 overflow-hidden">
            <div
              className="fixed inset-0"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)', backdropFilter: 'blur(2px)' }}
              onClick={() => setShowSubmitModal(false)}
            />

            <div className="fixed inset-0 flex items-start justify-center z-90 p-4 overflow-y-auto">
              {/* Desktop: big top offset; Mobile: smaller top offset */}
              <div className="submit-project-modal bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-8 relative border border-gray-100 my-4 custom-scrollbar" style={{ marginTop: '120px', maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
                <button onClick={() => setShowSubmitModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-white/60 rounded-full p-1 shadow-sm">
                  <X className="h-5 w-5" />
                </button>

                <div className="mb-4">
                  <h3 className="text-2xl font-semibold text-gray-900">Submit Project</h3>
                  <p className="text-sm text-gray-600 mt-1">Share your project details for this hackday — include repo link, short demo description and optional media links.</p>
                </div>

                <div className="grid grid-cols-1 gap-4 text-sm">
                  <input placeholder="Project Title (max 32 chars)" value={projectTitle} onChange={e => setProjectTitle(e.target.value.slice(0, 32))} className="w-full p-4 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />

                  {/* Auto-sizing textarea for demo description (min 2 rows) */}
                  <textarea
                    placeholder="Demo Short Description (max 140 chars)"
                    value={demoDescription}
                    onChange={e => setDemoDescription(e.target.value.slice(0, 140))}
                    rows={2}
                    onInput={(e) => {
                      const ta = e.target as HTMLTextAreaElement;
                      ta.style.height = 'auto';
                      const newHeight = Math.min(ta.scrollHeight, 260);
                      ta.style.height = `${newHeight}px`;
                    }}
                    className="w-full p-4 border rounded-lg text-sm resize-none overflow-auto shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300 min-h-[60px]"
                  />

                  <input placeholder="Github / Gitlab Repo (required)" value={githubRepoLink} onChange={e => setGithubRepoLink(e.target.value)} className="w-full p-4 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />

                  {/* Bounty Selection */}
                  {eventBounties.length > 0 && (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Select Bounties to Submit For
                      </label>
                      <div className="space-y-2">
                        {eventBounties.map((bounty) => (
                          <label key={bounty.id} className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-gray-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedBountyIds.includes(bounty.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedBountyIds(prev => [...prev, bounty.id]);
                                } else {
                                  setSelectedBountyIds(prev => prev.filter(id => id !== bounty.id));
                                }
                              }}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{bounty.title}</span>
                                {bounty.prizes.length > 0 && (
                                  <span className="text-sm font-semibold text-purple-600">
                                    {bounty.prizes[0].currency} {bounty.prizes[0].amount}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                <MarkdownRenderer content={bounty.description} />
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <input placeholder="Demo URL (optional)" value={demoUrl} onChange={e => setDemoUrl(e.target.value)} className="flex-1 p-3 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                    <input placeholder="Slides URL (optional)" value={slidesUrl} onChange={e => setSlidesUrl(e.target.value)} className="flex-1 p-3 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                  </div>

                  <div className="flex gap-3">
                    <input placeholder="Video URL (optional)" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} className="flex-1 p-3 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                    <input placeholder="Tech Stack (max 140 words)" value={techStack} onChange={e => setTechStack(e.target.value.slice(0, 140 * 7))} className="flex-1 p-3 border rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
                  </div>

                  <div className="flex justify-end gap-3 mt-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowSubmitModal(false)} className="px-4 py-2">Cancel</Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!githubRepoLink) {
                          setError('Github repo is required');
                          return;
                        }
                        let repo = githubRepoLink.trim();
                        if (!/^https?:\/\//i.test(repo)) repo = `https://${repo}`;
                        if (!/github\.com|gitlab\.com/i.test(repo)) {
                          setError('Repo must be github.com or gitlab.com');
                          return;
                        }

                        const teamId = submitTeamId;
                        if (!teamId) {
                          setError('Team not found');
                          return;
                        }

                        submitProjectMutation.mutate({
                          teamId,
                          projectData: {
                            projectTitle: projectTitle || undefined,
                            githubRepoLink: repo,
                            demoDescription: demoDescription || undefined,
                            demoUrl: demoUrl || undefined,
                            slidesUrl: slidesUrl || undefined,
                            videoUrl: videoUrl || undefined,
                            techStack: techStack || undefined,
                            bountyIds: selectedBountyIds.length > 0 ? selectedBountyIds : undefined,
                          }
                        });
                      }}
                      disabled={submitProjectMutation.isPending}
                    >
                      {submitProjectMutation.isPending ? 'Submitting...' : 'Submit Project'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Registration Modal */}
        {showRegistrationModal && registeringEventId && (
          <TeamRegistrationForm
            eventId={registeringEventId}
            eventTitle={registeringEventTitle}
            onSubmit={handleRegistrationSubmit}
            onCancel={handleRegistrationCancel}
            isSubmitting={registerMutation.isPending || updateTeamMutation.isPending}
            isFullPage={false}
            isEditMode={isEditMode}
            teamId={editTeamId || undefined}
            onUnregister={() => {
              setUnregisteringEventId(registeringEventId);
              setShowUnregisterModal(true);
              setShowRegistrationModal(false);
            }}
          />
        )}

        {/* Unregister/Leave Event Confirmation Modal */}
        {showUnregisterModal && unregisteringEventId && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(2px)'
              }}
              onClick={() => {
                if (!unregisterMutation.isPending) {
                  setShowUnregisterModal(false);
                  setUnregisteringEventId(null);
                }
              }}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 pointer-events-none">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200 pointer-events-auto">
                {/* Close Button */}
                {!unregisterMutation.isPending && (
                  <button
                    onClick={() => {
                      setShowUnregisterModal(false);
                      setUnregisteringEventId(null);
                    }}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}

                {/* Icon and Title */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Leave Event?
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to leave this event? You will be removed from your team and will need to register again if you change your mind.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowUnregisterModal(false);
                      setUnregisteringEventId(null);
                    }}
                    disabled={unregisterMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUnregister}
                    disabled={unregisterMutation.isPending}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {unregisterMutation.isPending ? 'Leaving...' : 'Leave Event'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const CurrentEventsBounties = Events;

export default Events;
